import { GET_AVAILABLE_RATE_GROUPS } from "@/actions/GET_AVAILABLE_RATE_GROUPS.ts";
import { GET_AVAILABLE_RATE_GROUPS_WITH_LOWEST_PRICE } from "@/actions/GET_AVAILABLE_RATE_GROUPS_WITH_LOWEST_PRICE.ts";
import { URLParam } from "@/helpers/interfaces/URLParam.ts";
import { parseDateString } from "@/helpers/parseDateString.ts";
import { AvailableRateGroupsInput, InputMaybe, RateGroup, RateGroupFilter } from "@/helpers/types/types.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { useLazyQuery } from "@apollo/client";
import format from "date-fns/format";
import { useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

export interface GetAvailableRateGroupsResponse {
  availableRateGroups: RateGroup[];
}
export interface GetAvailableRateGroupsInput {
  input: AvailableRateGroupsInput;
  // as date
  from: string;
  // as date
  until: string;
  filter?: InputMaybe<RateGroupFilter[]>;
}
export const useLazyGetAvailableRateGroups = () => {
  const { defaultDateRange, setAvailableRateGroups, rootAttributes } = useEngineStore(
    useShallow((state) => ({
      defaultDateRange: state.defaultDateRange,
      setAvailableRateGroups: state.setAvailableRateGroups,
      rootAttributes: state.rootAttributes,
      // TODO: add when guest count is back
      // defaultAdults: state.defaultAdults,
    })),
  );

  const useClassicRGQuery = rootAttributes[RootAttribute.UseClassicRatePlanDetails];
  const RGQuery = useClassicRGQuery ? GET_AVAILABLE_RATE_GROUPS_WITH_LOWEST_PRICE : GET_AVAILABLE_RATE_GROUPS;

  const [fetchAvailableRGs, { data: availableRGs, loading: getAvailableRGsLoading }] = useLazyQuery<
    GetAvailableRateGroupsResponse,
    GetAvailableRateGroupsInput
  >(RGQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (response) => setAvailableRateGroups(response.availableRateGroups),
    onError: (error) => {
      error && setAvailableRateGroups([]);
      console.error("Error fetching GET_AVAILABLE_RATE_GROUPS:", error);
    },
  });

  const handleFetchAvailableRGs = async (overrideDates?: [Date, Date]) => {
    const checkIn = overrideDates?.[0] ?? defaultDateRange[0];
    const checkOut = overrideDates?.[1] ?? defaultDateRange[1];

    if (!checkIn || !checkOut || (typeof checkIn === "string" && typeof checkOut === "string")) {
      return;
    }
    try {
      const urlSearchParams = new URLSearchParams(window.location.search);

      const rateGroupCodesParam = urlSearchParams.getAll(URLParam.RateGroup);
      const isParcelParam = urlSearchParams.get(URLParam.Parcel);
      const accTypeParam = urlSearchParams.get(URLParam.AccommodationType);
      // TODO: add when guest count is back
      // const persons = Array.from({ length: defaultAdults })?.map(() => ({ type: PersonType.Adult, age: 20 }));

      const filter = [];
      rateGroupCodesParam.length && filter.push({ code: { in: rateGroupCodesParam } });
      isParcelParam && filter.push({ isParcel: { equals: isParcelParam === "true" } });
      accTypeParam && filter.push({ accommodationType__code: { equals: accTypeParam } });

      await fetchAvailableRGs({
        variables: {
          input: {
            checkIn: format(checkIn, "yyyy-MM-dd"),
            checkOut: format(checkOut, "yyyy-MM-dd"),
            // TODO: add when guest count is back
            // persons,
          },
          from: format(checkIn, "yyyy-MM-dd"),
          until: format(checkOut, "yyyy-MM-dd"),
          filter,
        },
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // gets data from URL params and fetches available rate groups
  // if no data in URL, useURLParams will first set the data in the params and then fetch
  const handleInitialFetchAvailableRGs = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const startDateParam = urlParams.get(URLParam.StartDate);
    const endDateParam = urlParams.get(URLParam.EndDate);
    // TODO: add when guest counts is back
    // const guestCountParam = urlParams.get(URLParam.GuestCount);

    const rateGroupCodesParam = urlParams.getAll(URLParam.RateGroup);
    const isParcelParam = urlParams.get(URLParam.Parcel);
    const accTypeParam = urlParams.get(URLParam.AccommodationType);

    // TODO: add when guest count is back: || !guestCountParam
    if (!startDateParam || !endDateParam) return;

    try {
      // TODO: add when guest count is back
      // const persons = Array.from({ length: Number(guestCountParam) })?.map(() => ({
      //   type: PersonType.Adult,
      //   age: 20,
      // }));

      const filter = [];
      rateGroupCodesParam.length && filter.push({ code: { in: rateGroupCodesParam } });
      isParcelParam && filter.push({ isParcel: { equals: isParcelParam === "true" } });
      accTypeParam && filter.push({ accommodationType__code: { equals: accTypeParam } });

      await fetchAvailableRGs({
        variables: {
          input: {
            checkIn: format(parseDateString(startDateParam), "yyyy-MM-dd"),
            checkOut: format(parseDateString(endDateParam), "yyyy-MM-dd"),
            // TODO: add when guest count is back
            // persons,
          },
          from: format(parseDateString(startDateParam), "yyyy-MM-dd"),
          until: format(parseDateString(endDateParam), "yyyy-MM-dd"),
          filter,
        },
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    // proper dependency array
    // eslint-disable-next-line
  }, []);

  return {
    availableRGs: availableRGs || [],
    getAvailableRGsLoading,
    handleFetchAvailableRGs,
    handleInitialFetchAvailableRGs,
  };
};
